import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, Circle, Loader2, LogOut, Music2, Target, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const OBJECTIVE_LABELS = { pendente: "Pendente", em_andamento: "Em andamento", concluido: "Concluído" };
const ATTENDANCE_LABELS = { presente: "Presente", falta: "Falta", falta_justificada: "Falta justificada" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [lessons, setLessons] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/me")
      .then((r) => {
        setMe(r.data);
        if (r.data.students.length > 0) setStudentId(r.data.students[0].id);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const loadStudent = useCallback((sid) => {
    setLoading(true);
    Promise.all([
      api.get(`/portal/students/${sid}/summary`),
      api.get(`/portal/students/${sid}/lessons`),
    ]).then(([s, l]) => {
      setSummary(s.data);
      setLessons(l.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (studentId) loadStudent(studentId);
  }, [studentId, loadStudent]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-muted/40" data-testid="portal-page">
      <header className="sticky top-0 z-10 border-b bg-background">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:block" data-testid="portal-user-name">{me?.user?.name || user?.name}</span>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="portal-logout-button">
              <LogOut size={14} className="mr-1" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-6 px-4 py-6">
        {me && me.students.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="portal-student-selector">
            {me.students.map((s) => (
              <Button
                key={s.id}
                variant={s.id === studentId ? "default" : "outline"}
                size="sm"
                onClick={() => setStudentId(s.id)}
                data-testid={`portal-student-tab-${s.id}`}
              >
                <UserRound size={14} className="mr-1" /> {s.full_name}
              </Button>
            ))}
          </div>
        )}

        {me && me.students.length === 0 && (
          <p className="border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="portal-no-students">
            Nenhum aluno vinculado à sua conta. Procure a secretaria do IMJ.
          </p>
        )}

        {loading && (
          <div className="flex justify-center py-12" data-testid="portal-loading">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && summary && (
          <>
            <section data-testid="portal-enrollments">
              <h2 className="font-heading text-base font-semibold md:text-lg">{summary.student.full_name}</h2>
              <div className="mt-2 space-y-2">
                {summary.enrollments.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="portal-no-enrollment">Nenhuma matrícula ativa no momento.</p>
                )}
                {summary.enrollments.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 border bg-background p-4" data-testid={`portal-enrollment-${e.id}`}>
                    <Music2 size={18} className="shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-semibold">{e.course_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Prof. {e.teacher_name} · {e.weekday_label}, {e.start_time}–{e.end_time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-2 gap-3" data-testid="portal-indicators">
              <div className="border bg-background p-4" data-testid="portal-frequency">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Frequência</p>
                <p className="mt-1 font-heading text-3xl font-bold" data-testid="portal-frequency-percent">{summary.frequency.percentual}%</p>
                <p className="mt-1 text-xs text-muted-foreground" data-testid="portal-frequency-detail">
                  {summary.frequency.presencas} presenças · {summary.frequency.faltas} faltas
                </p>
              </div>
              <div className="border bg-background p-4" data-testid="portal-progress">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Progresso</p>
                <p className="mt-1 font-heading text-3xl font-bold" data-testid="portal-progress-percent">{summary.progress.percentual}%</p>
                <p className="mt-1 text-xs text-muted-foreground" data-testid="portal-progress-detail">
                  {summary.progress.concluidos} de {summary.progress.total} objetivos concluídos
                </p>
              </div>
            </section>

            {summary.objectives.length > 0 && (
              <section data-testid="portal-objectives">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                  <Target size={16} className="text-primary" /> Objetivos pedagógicos
                </h3>
                <div className="mt-2 space-y-1.5">
                  {summary.objectives.map((o) => (
                    <div key={o.id} className="flex items-center gap-2 border bg-background px-3 py-2 text-sm" data-testid={`portal-objective-${o.id}`}>
                      {o.status === "concluido"
                        ? <CheckCircle2 size={15} className="shrink-0 text-emerald-600" />
                        : <Circle size={15} className="shrink-0 text-muted-foreground" />}
                      <span className={o.status === "concluido" ? "text-muted-foreground line-through" : ""}>{o.title}</span>
                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">{OBJECTIVE_LABELS[o.status] || o.status}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {!loading && lessons && (
          <section className="space-y-4" data-testid="portal-lessons">
            <div>
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold">
                <CalendarDays size={16} className="text-primary" /> Próximas aulas
              </h3>
              <div className="mt-2 space-y-1.5">
                {lessons.upcoming.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="portal-no-upcoming">Nenhuma aula agendada.</p>
                )}
                {lessons.upcoming.map((l) => (
                  <div key={l.id} className="flex items-center justify-between border bg-background px-3 py-2 text-sm" data-testid={`portal-upcoming-${l.id}`}>
                    <span>{l.course_name} · Prof. {l.teacher_name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{fmtDate(l.date)} · {l.start_time}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-heading text-base font-semibold">Últimas aulas</h3>
              <div className="mt-2 space-y-1.5">
                {lessons.recent.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="portal-no-recent">Nenhuma aula realizada ainda.</p>
                )}
                {lessons.recent.map((l) => (
                  <div key={l.id} className="border bg-background px-3 py-2 text-sm" data-testid={`portal-recent-${l.id}`}>
                    <div className="flex items-center justify-between">
                      <span>{l.course_name} · Prof. {l.teacher_name}</span>
                      <span className={`shrink-0 text-xs ${l.attendance === "presente" ? "text-emerald-700" : "text-muted-foreground"}`}>
                        {fmtDate(l.date)} · {ATTENDANCE_LABELS[l.attendance] || l.attendance}
                      </span>
                    </div>
                    {l.diary_content && (
                      <p className="mt-1 border-l-2 border-primary/40 pl-2 text-xs text-muted-foreground">{l.diary_content}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
